import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaChevronRight, FaStickyNote, FaEnvelope, FaPhone, FaUserCheck } from "react-icons/fa";
import ActionIcon from "./ActionIcon";
import getParticipantStatusStyle from "../../../utils/getParticipantStatusStyle";
import "../../../css/View/ActiveParticipantsList.css";
import { useToast } from "../../../Contexts/ToastContext";
import useApi from "../../CustomHooks/useApi";
import { participantsAPI } from "../../../api/apiClient";
import { useTripContext } from "../TripContext";
import exportParticipantsToExcel from "./ExportParticipantsToExcel";

const ParticipantsList = ({ participants = [], tripName, variantName }) => {
  const [selectedGuid, setSelectedGuid] = useState(null);
  const [selectedTop, setSelectedTop] = useState(0);
  const [hoveredGuid, setHoveredGuid] = useState(null);
  const [hoveredTop, setHoveredTop] = useState(0);
  const [mode, setMode] = useState(null);
  const [statusMap, setStatusMap] = useState({});
  const { guid: tripGuid } = useParams();
  const navigate = useNavigate();
  const { showToast, clearToast } = useToast();
  const { callApi: saveAttendance, isLoading: isSavingAttendance } = useApi();
  const { updateParticipantsBulkInContext } = useTripContext();
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const handleBeforeUnload = (e) => { if (mode === "attendance") { e.preventDefault(); e.returnValue = ""; } };
    const handleBack = (e) => {
      if (mode === "attendance") {
        const confirmed = window.confirm("Czy na pewno chcesz przejść wstecz? Wszystkie zmiany nie będą zapisane.");
        if (!confirmed) { e.preventDefault(); window.history.pushState(null, "", window.location.href); }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBack);
    return () => { window.removeEventListener("beforeunload", handleBeforeUnload); window.removeEventListener("popstate", handleBack); };
  }, [mode]);

  useEffect(() => {
    const statuses = {};
    participants.forEach((p) => { statuses[p.guid] = p.status; });
    setStatusMap(statuses);
  }, [participants]);

  const countSummary = participants.reduce((acc, p) => {
    const status = mode === "attendance" ? statusMap[p.guid] : p.status;
    if (status === 2) acc.present++;
    else if (status === 3) acc.absent++;
    acc.total++;
    if (p.firstTime) acc.newbies++;
    return acc;
  }, { present: 0, absent: 0, total: 0, newbies: 0 });

  const filteredParticipants = participants.filter((p) => {
    const currentStatus = mode === "attendance" ? statusMap[p.guid] : p.status;
    if (filter === "present") return currentStatus === 2;
    if (filter === "absent") return currentStatus === 3;
    if (filter === "newbies") return !!p.firstTime;
    return true;
  });

  const previewGuid = hoveredGuid || selectedGuid;
  const previewTop = hoveredGuid ? hoveredTop : selectedTop;
  const previewParticipant = participants.find((p) => p.guid === previewGuid) || null;

  const getPreviewTop = (element) => {
    const container = element.closest(".participants-master-detail");
    if (!container) return 0;
    const rowCenter = element.getBoundingClientRect().top - container.getBoundingClientRect().top + element.getBoundingClientRect().height / 2;
    const previewHeight = 300;
    return Math.max(0, rowCenter - previewHeight / 2);
  };

  const cycleStatus = (guid) => setStatusMap((prev) => {
    const current = prev[guid];
    const next = current === 1 || current === undefined ? 2 : current === 2 ? 3 : 1;
    return { ...prev, [guid]: next };
  });
  const setAttendanceStatus = (guid, status) => setStatusMap((prev) => ({ ...prev, [guid]: status }));

  const startAttendance = () => {
    setHoveredGuid(null);
    setSelectedGuid(null);
    setMode("attendance");
    setFilter("all");
  };
  const cancelAttendance = () => {
    const statuses = {};
    participants.forEach((p) => { statuses[p.guid] = p.status; });
    setStatusMap(statuses);
    setMode(null);
    clearToast();
  };

  const handleSaveAttendance = async () => {
    const updates = participants.map((p) => ({ guid: p.guid, status: statusMap[p.guid] ?? p.status }));
    await saveAttendance(() => participantsAPI.setAttendance(updates, tripGuid), async () => {
      updateParticipantsBulkInContext(updates);
      clearToast();
      showToast("Obecność sprawdzona pomyślnie!", "success");
      setMode(null);
    });
  };

  const openParticipant = (p) => navigate(`/trips/${tripGuid}/variants/${p.variantGuid}/participants/${p.guid}`);
  const openReservation = (p) => navigate(`/trips/${tripGuid}/variants/${p.variantGuid}/registrations/${p.reservationGuid}`);

  return (
    <div className={`participants-list-container ${mode ? "mode-active" : ""}`}>
      <h2 className="participants-mobile-heading">Osoby</h2>
      <div className="participant-toolbar">
        <div className="participant-filter-bar">
          <div className="filter-toggle-group">
            <div className={`filter-toggle ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}><i className="fa-solid fa-users" /><span><strong>{countSummary.total}</strong><small>Os. uczestniczące</small></span></div>
            <div className={`filter-toggle ${filter === "present" ? "active" : ""}`} onClick={() => setFilter("present")}><i className="fa-solid fa-user-check" /><span><strong>{countSummary.present}</strong><small>Os. obecne</small></span></div>
            <div className={`filter-toggle ${filter === "absent" ? "active" : ""}`} onClick={() => setFilter("absent")}><i className="fa-solid fa-user-xmark" /><span><strong>{countSummary.absent}</strong><small>Os. nieobecne</small></span></div>
            <div className={`filter-toggle ${filter === "newbies" ? "active" : ""}`} onClick={() => setFilter("newbies")}><i className="fa-solid fa-hand-point-up" /><span><strong>{countSummary.newbies}</strong><small>Pierwszy raz</small></span></div>
          </div>
        </div>

        <div className="participants-desktop-actions">
          {mode !== "attendance" ? <>
            <div className="participants-mobile-attendance-trigger"><ActionIcon icon={<FaUserCheck />} label="Sprawdź obecność" onClick={startAttendance} /></div>
            <ActionIcon icon={<i className="fa-solid fa-file-excel" />} label="Eksport" onClick={() => exportParticipantsToExcel(participants, tripName, variantName)} />
          </> : <div className="attendance-toolbar-actions">
            <div className="attendance-toolbar-copy"><strong>Sprawdzanie obecności</strong><span>Oznacz każdą osobę jako obecną lub nieobecną.</span></div>
            <button type="button" className="attendance-cancel" onClick={cancelAttendance}>Anuluj</button>
            <button type="button" className="attendance-save" onClick={handleSaveAttendance} disabled={isSavingAttendance}><i className="fa-solid fa-floppy-disk" /> {isSavingAttendance ? "Zapisywanie..." : "Zapisz"}</button>
          </div>}
        </div>
      </div>

      <div className="participants-master-detail">
        <div className="participants-master-column">
          <div className="participants-column-title"><span className="participants-column-label">Lista osób</span>{mode !== "attendance" ? <button type="button" className="participants-attendance-start" onClick={startAttendance}><FaUserCheck /> Sprawdź obecność</button> : <span className="participants-attendance-mode">Sprawdzanie obecności</span>}</div>
          <ul className="participants-list">
            {filteredParticipants.map((p) => {
              const currentStatus = mode === "attendance" ? statusMap[p.guid] : p.status;
              const style = getParticipantStatusStyle(currentStatus);
              return (
                <li key={p.guid} className={`participant-item ${style.statusClass} ${previewGuid === p.guid ? "is-previewed" : ""} ${mode === "attendance" ? "participant-item--attendance" : ""}`} onMouseEnter={(e) => { if (!mode) { setHoveredGuid(p.guid); setHoveredTop(getPreviewTop(e.currentTarget)); } }} onMouseLeave={() => !mode && setHoveredGuid(null)}>
                  <div className="participant-row" onClick={(e) => {
                    if (mode === "attendance") cycleStatus(p.guid);
                    else { setSelectedGuid(p.guid); setSelectedTop(getPreviewTop(e.currentTarget.closest(".participant-item"))); }
                  }}>
                    <div className={`participant-name ${style.statusClass}`}>
                      <i className={`fa-solid ${style.icon} presence-icon ${style.statusClass}`} title={style.label} />
                      <span>{p.preferredName} {p.pronouns && <i>({p.pronouns})</i>}</span>
                      {p.firstTime && <span className="first-time-badge"><i className="fa-solid fa-hand-point-up" /> Pierwszy raz</span>}
                    </div>
                    {mode === "attendance" ? <div className="attendance-status-controls" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className={`attendance-status attendance-status--present ${currentStatus === 2 ? "active" : ""}`} onClick={() => setAttendanceStatus(p.guid, 2)}><i className="fa-solid fa-check" /><span>Obecna</span></button>
                      <button type="button" className={`attendance-status attendance-status--absent ${currentStatus === 3 ? "active" : ""}`} onClick={() => setAttendanceStatus(p.guid, 3)}><i className="fa-solid fa-xmark" /><span>Nieobecna</span></button>
                    </div> : <div className="participant-icons">{p.coordinatorNotes && <FaStickyNote className="note-icon" title="Notatki koordynatora" />}<FaChevronRight /></div>}
                  </div>
                  <div className="participant-mobile-details">
                    <div className="contact-info"><div className="contact-line"><FaEnvelope /> <span>{p.email}</span></div><div className="contact-line"><FaPhone /> <span>{p.phone}</span></div></div>
                    <button className="profile-link" onClick={(e) => { e.stopPropagation(); openParticipant(p); }}>Szczegóły</button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {previewParticipant && mode !== "attendance" && (
          <aside className="participant-preview-panel" style={{ top: `${previewTop}px` }}>
            <div className="participant-preview-heading participant-preview-heading--clickable" role="button" tabIndex={0} onClick={() => openParticipant(previewParticipant)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openParticipant(previewParticipant); }}>
              <div className="participant-preview-avatar"><i className="fa-solid fa-user" aria-hidden="true" /></div>
              <div><h3>{previewParticipant.preferredName}</h3>{previewParticipant.pronouns && <span>{previewParticipant.pronouns}</span>}</div>
              <FaChevronRight className="participant-preview-heading-chevron" />
            </div>
            <div className="participant-preview-contact-row">
              <div><FaEnvelope /><span><small>E-mail</small>{previewParticipant.email || "—"}</span></div>
              <div><FaPhone /><span><small>Telefon</small>{previewParticipant.phone || "—"}</span></div>
            </div>
            <div className="participant-preview-info">
              {previewParticipant.firstTime && <div className="participant-preview-first-time"><i className="fa-solid fa-hand-point-up" /><span>Pierwszy raz</span></div>}
              <button className="participant-preview-reservation" type="button" onClick={() => openReservation(previewParticipant)}><i className="fa-solid fa-clipboard-list" /><span><small>Rezerwacja</small>{previewParticipant.reservationCode || "Przejdź do rezerwacji"}</span><FaChevronRight /></button>
            </div>
            {previewParticipant.coordinatorNotes && <div className="participant-preview-note"><FaStickyNote /><span><small>Notatka os. koo.</small>{previewParticipant.coordinatorNotes}</span></div>}
          </aside>
        )}
      </div>

      {mode === "attendance" && <div className="mobile-attendance-action-bar"><span className="attendance-bottom-hint">Kliknij osobę, aby zmienić status: <strong>Obecna → Nieobecna → Nieoznaczona</strong></span><div className="attendance-bottom-buttons"><button className="cancel-button" onClick={cancelAttendance}>Anuluj</button><button className="save-button" onClick={handleSaveAttendance} disabled={isSavingAttendance}>{isSavingAttendance ? "Zapisywanie..." : "Zapisz obecności"}</button></div></div>}
    </div>
  );
};

export default ParticipantsList;
